/**
 * Feature Flags Utility
 *
 * Centralized management of feature flags for the application.
 * This allows us to enable/disable features without code changes.
 *
 * All feature flags should use explicit string comparison to avoid
 * issues with truthy/falsy values from environment variables.
 */

export const featureFlags = {
  /**
   * Real-time WebSocket features flag
   *
   * When enabled:
   * - WebSocket connections are established for real-time updates
   * - Live notifications for new deals, comments, etc.
   *
   * When disabled (V1 launch state):
   * - No WebSocket connections
   * - Users must refresh to see updates
   *
   * To enable in the future:
   * 1. Set REACT_APP_FEATURE_REALTIME_ENABLED=true
   * 2. Set REACT_APP_WS_URL to direct Cloud Run URL (not Firebase Hosting)
   * 3. Verify CORS configuration
   */
  realtimeEnabled: process.env.REACT_APP_FEATURE_REALTIME_ENABLED === 'true',
} as const;

// Type-safe feature flag keys
export type FeatureFlagKey = keyof typeof featureFlags;

/**
 * Helper function to check if a feature is enabled
 * @param flag - The feature flag key to check
 * @returns boolean indicating if the feature is enabled
 */
export const isFeatureEnabled = (flag: FeatureFlagKey): boolean => {
  return featureFlags[flag];
};
